import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaUsers,
  FaUserTimes,
  FaCalendarAlt,
  FaBuilding,
} from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import HeaderMain from "../../dasboard-component/Components/HeaderMain";
import ImageModal from "../../common/FullScreenImageModal.jsx";
import VerificationIdModal from "../VerificationIdModal.jsx";
import { toast } from "react-toastify";

import styles from "./UserInstituteDetails.module.css";

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const UserInstituteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [institute, setInstitute] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [leftUsers, setLeftUsers] = useState([]);
  const [arenas, setArenas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

  useEffect(() => {
    fetchInstituteDetails();
    fetchInstituteUsers();
    fetchInstituteArenas();
  }, [id]);

  const fetchInstituteDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes/${id}`,
        {
          withCredentials: true,
        }
      );
      setInstitute(response.data);
    } catch (error) {
      console.error("Error fetching institute details:", error);
      setError("Failed to fetch institute details");
    }
  };

  const fetchInstituteUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${id}/users`,
        {
          withCredentials: true,
        }
      );
      setActiveUsers(response.data.activeUsers);
      setLeftUsers(response.data.leftUsers);
    } catch (error) {
      console.error("Error fetching institute users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstituteArenas = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes/${id}/arenas`,
        {
          withCredentials: true,
        }
      );
      setArenas(response.data);
    } catch (error) {
      console.error("Error fetching institute arenas:", error);
    }
  };

  const handleRequestAffiliation = () => {
    setIsVerificationModalOpen(true);
  };

  const handleVerificationSubmit = async (verificationId) => {
    setIsVerificationModalOpen(false);
    setRequesting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/institute/request-affiliation`,
        {
          institute_id: id,
          verification_id: verificationId,
        },
        { withCredentials: true }
      );
      toast("Affiliation request sent successfully!");
    } catch (error) {
      console.error("Error requesting affiliation:", error);
      toast.error(error.response?.data?.message || "Error sending request");
    } finally {
      setRequesting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <HeaderMain />
        <div className={styles.loadingContainer}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className={styles.loadingText} style={{ marginTop: "1rem" }}>
              Loading institute details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!institute) {
    return (
      <div className={styles.container}>
        <HeaderMain />
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Institute not found</p>
          <button
            onClick={() => navigate("/institute-dashboard")}
            className={styles.backToListButton}
          >
            Back to Institute List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <HeaderMain />
      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.headerCard}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <button
                onClick={() => navigate("/institute-dashboard")}
                className={styles.backButton}
              >
                <FaArrowLeft />
              </button>
              {institute.logo && (
                <img
                  src={institute.logo}
                  alt={`${institute.name} logo`}
                  className={styles.instituteLogo}
                  onClick={() => handleImageClick(institute.logo)}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <div className={styles.instituteInfo}>
                <h1 className={styles.instituteTitle}>{institute.name}</h1>
                <p className={styles.instituteType}>
                  <FaBuilding />
                  {institute.institute_type_name}
                </p>
              </div>
            </div>
            <button
              onClick={handleRequestAffiliation}
              disabled={requesting}
              className={styles.affiliationButton}
            >
              {requesting ? "Sending Request..." : "Request Affiliation"}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className={styles.errorMessage}>{error}</div>}

        {/* Tabs */}
        <div className={styles.tabsCard}>
          <nav className={styles.tabNav}>
            {[
              { key: "details", label: "Details", icon: FaBuilding },
              {
                key: "users",
                label: `Community (${activeUsers.length + leftUsers.length})`,
                icon: FaUsers,
              },
              {
                key: "arenas",
                label: `Arenas (${arenas.length})`,
                icon: FaBuilding,
              },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`${styles.tabButton} ${
                  activeTab === key ? styles.activeTab : ""
                }`}
              >
                <Icon />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          <div className={styles.tabContent}>
            {/* Details Tab */}
            {activeTab === "details" && (
              <div className={styles.detailsSection}>
                {/* Basic Information */}
                <div className={styles.detailsGrid}>
                  <div className={styles.infoSection}>
                    <h3 className={styles.sectionTitle}>Basic Information</h3>
                    <div className={styles.infoCard}>
                      <div className={styles.infoItem}>
                        <FaCalendarAlt className={styles.infoIcon} />
                        <div className={styles.infoContent}>
                          <label className={styles.infoLabel}>
                            Established
                          </label>
                          <p className={styles.infoText}>
                            {formatDate(institute.date_established)}
                          </p>
                        </div>
                      </div>
                      <div className={styles.infoItem}>
                        <FaBuilding className={styles.infoIcon} />
                        <div className={styles.infoContent}>
                          <label className={styles.infoLabel}>
                            Contact Person
                          </label>
                          <p className={styles.infoText}>
                            {institute.contact_person}
                          </p>
                        </div>
                      </div>
                      {institute.company_registration_number && (
                        <div className={styles.infoItem}>
                          <FaBuilding className={styles.infoIcon} />
                          <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>
                              Registration Number
                            </label>
                            <p className={styles.infoText}>
                              {institute.company_registration_number}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.infoSection}>
                    <h3 className={styles.sectionTitle}>Contact Information</h3>
                    <div className={styles.infoCard}>
                      <div className={styles.infoItem}>
                        <FaEnvelope className={styles.infoIcon} />
                        <span className={styles.infoText}>
                          {institute.email}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <FaPhone className={styles.infoIcon} />
                        <span className={styles.infoText}>
                          {Array.isArray(institute.telephone)
                            ? institute.telephone.join(", ")
                            : institute.telephone || "Not provided"}
                        </span>
                      </div>
                      {institute.website && (
                        <div className={styles.infoItem}>
                          <FaGlobe className={styles.infoIcon} />
                          <a
                            href={institute.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.infoLink}
                          >
                            {institute.website}
                          </a>
                        </div>
                      )}
                      <div className={styles.socialLinks}>
                        {institute.facebook_url && (
                          <a
                            href={institute.facebook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaFacebook
                              className={styles.socialIcon}
                              style={{ color: "#1877f2" }}
                            />
                          </a>
                        )}
                        {institute.instagram_url && (
                          <a
                            href={institute.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaInstagram
                              className={styles.socialIcon}
                              style={{ color: "#e4405f" }}
                            />
                          </a>
                        )}
                        {institute.x_url && (
                          <a
                            href={institute.x_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaTwitter
                              className={styles.socialIcon}
                              style={{ color: "#1da1f2" }}
                            />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address & Map */}
                <div className={styles.addressSection}>
                  <h3 className={styles.sectionTitle}>Address & Location</h3>
                  <div className={styles.addressCard}>
                    <div className={styles.addressContent}>
                      <FaMapMarkerAlt className={styles.addressIcon} />
                      <div>
                        <p className={styles.addressText}>
                          {[
                            institute.plot_no,
                            institute.street,
                            institute.area,
                            institute.city,
                            institute.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        {institute.coordinates && (
                          <p className={styles.coordinates}>
                            Coordinates: {institute.coordinates}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Map */}
                    {institute.coordinates && (
                      <div className={styles.mapContainer}>
                        <MapContainer
                          center={institute.coordinates
                            .split(",")
                            .map((coord) => parseFloat(coord.trim()))}
                          zoom={15}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          />
                          <Marker
                            position={institute.coordinates
                              .split(",")
                              .map((coord) => parseFloat(coord.trim()))}
                          >
                            <Popup>
                              <div className="text-center">
                                <strong>{institute.name}</strong>
                                <br />
                                {institute.city}, {institute.country}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* About */}
                {institute.about_institute && (
                  <div className={styles.aboutSection}>
                    <h3 className={styles.sectionTitle}>About the Institute</h3>
                    <div className={styles.aboutCard}>
                      <p className={styles.aboutText}>
                        {institute.about_institute}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Community Tab */}
            {activeTab === "users" && (
              <div className={styles.communitySection}>
                {/* Active Users */}
                <div>
                  <h3 className={styles.communityHeader}>
                    <FaUsers className={styles.communityIcon} />
                    Active Members ({activeUsers.length})
                  </h3>
                  {activeUsers.length > 0 ? (
                    <div className={styles.usersGrid}>
                      {activeUsers.map((user) => (
                        <div key={user.id} className={styles.userCard}>
                          <div className={styles.userCardContent}>
                            <div className={styles.userAvatar}>
                              <FaUsers className={styles.userAvatarIcon} />
                            </div>
                            <h4 className={styles.userName}>
                              {user.first_name} {user.middle_name}{" "}
                              {user.last_name}
                            </h4>
                            <p className={styles.userUsername}>
                              @{user.user_name}
                            </p>
                            <p className={styles.userDetails}>
                              {user.age} years • {user.gender}
                            </p>
                            <p className={styles.userJoinDate}>
                              Joined: {formatDate(user.joined_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptyState}>
                      No active members found.
                    </p>
                  )}
                </div>

                {/* Former Users */}
                {leftUsers.length > 0 && (
                  <div>
                    <h3 className={styles.communityHeader}>
                      <FaUserTimes className={styles.communityIconFormer} />
                      Former Members ({leftUsers.length})
                    </h3>
                    <div className={styles.usersGrid}>
                      {leftUsers.map((user) => (
                        <div key={user.id} className={styles.userCardFormer}>
                          <div className={styles.userCardContent}>
                            <div className={styles.userAvatarFormer}>
                              <FaUserTimes
                                className={styles.userAvatarIconFormer}
                              />
                            </div>
                            <h4 className={styles.userNameFormer}>
                              {user.first_name} {user.middle_name}{" "}
                              {user.last_name}
                            </h4>
                            <p className={styles.userUsernameFormer}>
                              @{user.user_name}
                            </p>
                            <p className={styles.userJoinDate}>
                              Joined: {formatDate(user.joined_at)}
                            </p>
                            <p className={styles.userLeftDate}>
                              Left: {formatDate(user.left_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Arenas Tab */}
            {activeTab === "arenas" && (
              <div className={styles.arenasSection}>
                <div>
                  <h3 className={styles.sectionTitle}>Institute Arenas</h3>
                  {arenas.length > 0 ? (
                    <div className={styles.arenasGrid}>
                      {arenas.map((arena) => (
                        <div key={arena.id} className={styles.arenaCard}>
                          {/* Arena Header */}
                          <div className={styles.arenaHeader}>
                            <div className={styles.arenaMainInfo}>
                              {arena.logo ? (
                                <img
                                  src={arena.logo}
                                  alt={arena.Arena_name}
                                  className={styles.arenaLogo}
                                  onClick={() => handleImageClick(arena.logo)}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className={styles.arenaLogoPlaceholder}>
                                  <svg
                                    className={styles.arenaLogoIcon}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                  </svg>
                                </div>
                              )}
                              <div className={styles.arenaInfo}>
                                <h4 className={styles.arenaName}>
                                  {arena.Arena_name}
                                </h4>
                                <p className={styles.arenaLocation}>
                                  <FaMapMarkerAlt />
                                  {arena.city && arena.country
                                    ? `${arena.city}, ${arena.country}`
                                    : "Location not specified"}
                                </p>
                                <p className={styles.arenaContact}>
                                  <FaEnvelope />
                                  {arena.email}
                                </p>
                                {arena.telephone && (
                                  <p className={styles.arenaContact}>
                                    <FaPhone />
                                    {arena.telephone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Arena About */}
                          {arena.about_arena && (
                            <div className={styles.arenaAbout}>
                              <p className={styles.arenaAboutText}>
                                {arena.about_arena}
                              </p>
                            </div>
                          )}

                          {/* View Details Button */}
                          <button
                            onClick={() =>
                              navigate(`/arena-details/${arena.id}`)
                            }
                            className={styles.viewDetailsButton}
                          >
                            View Details
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyArenas}>
                      <svg
                        className={styles.emptyIcon}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <p className={styles.emptyText}>
                        No arenas registered yet
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ImageModal
        images={modalImage}
        initialIndex={0}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        alt="Institute/Arena Logo"
        isBase64={false}
      />

      <VerificationIdModal
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        onSubmit={handleVerificationSubmit}
        instituteName={institute?.name}
        instituteType={institute?.institute_type_name}
      />
    </div>
  );
};

export default UserInstituteDetails;
